import {
  Popover as AntPopover,
  type PopoverProps as AntPopoverProps,
} from 'antd';
import FocusTrap from 'focus-trap-react';
import React, {
  forwardRef,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

export interface IPopoverProps extends AntPopoverProps {
  children: React.ReactElement<HTMLButtonElement>;
  content: React.ReactNode;
  /**
   * Se `true`, al mount del popover il focus va sul primo elemento form-like
   * trovato nel contenuto (input/textarea/combobox/switch/contenteditable).
   * Utile per popover che ospitano un form: senza questo flag, da tastiera
   * l'utente atterra sul container del trap (tabindex=-1) e deve Tabbare di
   * nuovo per arrivare al campo.
   * @default false
   */
  autoFocusField?: boolean;
  /**
   * Se `true`, permette i click fuori dal container del FocusTrap senza
   * disattivarlo (Tab/Escape restano gestiti). Necessario per popover che
   * ospitano widget AntD con dropdown in portal (Select, DatePicker,
   * AutoComplete): senza questo flag il click sulle option della dropdown
   * verrebbe bloccato dal trap e l'utente sarebbe costretto a usare la
   * tastiera.
   * @default false
   */
  allowPortalClicks?: boolean;
}

export interface IPopoverHandle {
  close: () => void;
}

/**
 * Avvolge il `title` del Popover in uno `<span id>` per legarlo al
 * contenitore del contenuto via `aria-labelledby`. Gestisce sia il caso
 * `ReactNode` sia il caso `RenderFunction` di AntPopover. Restituisce
 * `undefined` quando non c'è un title da mostrare.
 */
function wrapPopoverTitle(
  title: AntPopoverProps['title'],
  titleId: string,
): AntPopoverProps['title'] {
  if (title === undefined || title === null || title === '') {
    return undefined;
  }
  if (typeof title === 'function') {
    return () => <span id={titleId}>{title()}</span>;
  }
  return <span id={titleId}>{title as React.ReactNode}</span>;
}

/**
 * Popover personalizzato che gestisce l'accessibilità: focus trap, ESC per
 * chiudere, click esterno per chiudere, e attributi ARIA che legano il
 * trigger al contenuto annunciato come dialog.
 *
 * Il children DEVE essere un elemento button (o composito che inoltra ref +
 * attributi ARIA). Vengono iniettati automaticamente:
 *   - `aria-expanded` (true/false)
 *   - `aria-haspopup="dialog"`
 *   - `aria-controls` → id del container del contenuto
 *
 * NOTA sul FocusTrap: AntPopover renderizza `title` e `content` in slot
 * DOM separati. Il FocusTrap qui wrappa solo `content`, quindi eventuali
 * elementi focusabili nel `title` (es. button) NON sono inclusi nel ciclo
 * Tab. Il pattern d'uso supportato è `title` come ReactNode statico (testo,
 * tag, icone non interattive). Per UI interattive nell'header serve un
 * refactor che inglobi title + content in un unico container.
 */
export const Popover = forwardRef(
  (props: IPopoverProps, ref: React.Ref<IPopoverHandle>) => {
    const {
      children,
      content,
      title,
      autoFocusField,
      allowPortalClicks,
      ...otherProps
    } = props;
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const dialogId = useId();
    const titleId = useId();

    useImperativeHandle(ref, () => ({
      close: () => {
        setIsOpen(false);
      },
    }));

    const hasTitle = title !== undefined && title !== null && title !== '';

    const wrappedTitle = wrapPopoverTitle(title, titleId);

    return (
      <AntPopover
        {...otherProps}
        title={wrappedTitle}
        trigger={['click']}
        open={isOpen}
        destroyTooltipOnHide={{
          keepParent: true,
        }}
        onOpenChange={nextIsOpen => {
          setIsOpen(nextIsOpen);
        }}
        content={
          <FocusTrap
            active={isOpen}
            focusTrapOptions={{
              escapeDeactivates: () => {
                setIsOpen(false);
                return true;
              },
              fallbackFocus: () => containerRef.current!,
              ...(autoFocusField && {
                initialFocus: () => {
                  const el = containerRef.current?.querySelector<HTMLElement>(
                    'input:not([type="hidden"]), textarea, [role="combobox"], [role="switch"], [contenteditable="true"]',
                  );
                  return el ?? containerRef.current!;
                },
              }),
              setReturnFocus: previous => {
                return previous ?? buttonRef.current!;
              },
              ...(allowPortalClicks && { allowOutsideClick: true }),
            }}
          >
            <div // NOSONAR
              id={dialogId}
              // <dialog> HTML richiede showModal() imperativo, incompatibile
              // con il rendering controllato da AntPopover. role="dialog" +
              // aria-modal + FocusTrap è il pattern WAI-ARIA APG standard per
              // dialog non-native.
              role="dialog"
              aria-modal="true"
              aria-labelledby={hasTitle ? titleId : undefined}
              tabIndex={-1}
              ref={containerRef}
            >
              {content}
            </div>
          </FocusTrap>
        }
      >
        {React.cloneElement(children as React.ReactElement, {
          ref: buttonRef,
          'aria-expanded': isOpen,
          'aria-haspopup': 'dialog',
          'aria-controls': isOpen ? dialogId : undefined,
        })}
      </AntPopover>
    );
  },
);

Popover.displayName = 'Popover';
